import { NotificationService, type Range, sleep } from '@capsicum/common';
import { type FC, useCallback } from 'react';

import { type GamifiationUserRewardType, type GlobalRewardType, type RewardPointsSnackbarProps } from '../models';
import { getRewardPointsSnackbarProps } from '../utils';

export type UseGamificationListenersType = {
	gamificationRewardEventHandler: (e: GamifiationUserRewardType) => void;
};

export const useGamificationListeners = (Snackbar: FC<RewardPointsSnackbarProps>): UseGamificationListenersType => {
	const deliverSnackbar = useCallback(
		(snackbarProps: RewardPointsSnackbarProps, snackbarPriority: Range<10>[number]): Promise<string> =>
			sleep(3000)
				.then(() =>
					NotificationService.addNotification({
						component: <Snackbar {...snackbarProps} />,
						priority: snackbarPriority,
						timeout: 3000
					})
				)
				.then(() => Promise.resolve('Added to NotificationService Queue')),
		[Snackbar]
	);

	const deliverGlobalRewardSnackbars = useCallback(
		async (globalReward?: GlobalRewardType) => {
			if (!!globalReward && globalReward.details && globalReward.details.length > 0) {
				for (let i = 0; i < globalReward.details.length; i++) {
					const detail = globalReward.details[i]!;
					const rewardProps = getRewardPointsSnackbarProps(detail.amount, detail.id);

					await deliverSnackbar(rewardProps, 1);
				}
			}
		},
		[deliverSnackbar]
	);

	const gamificationRewardEventHandler = useCallback(
		async (event: GamifiationUserRewardType) => {
			const globalReward = event.payload.globalReward;

			//deliver Glogal Reward Snackbars first if there is any
			await deliverGlobalRewardSnackbars(globalReward);
		},
		[deliverGlobalRewardSnackbars]
	);

	return {
		gamificationRewardEventHandler
	};
};
